import { useCallback, useRef, useState } from "react";

import type {
    DiagnosisClassification,
    DiagnosisSource,
    HubDiagnosis
} from "@/components/diagnosis-hub/types";
import {
    deleteVisitDiagnosisById,
    fetchDiagnosisHub,
    updateDiagnosisClassification
} from "@/lib/api";

interface DeletedItem {
    diagnosis: HubDiagnosis;
    source: DiagnosisSource;
    /** Original index in the diagnoses array (for undo re-insertion) */
    index: number;
}

export function useDiagnosisHub(visitId: string) {
    const [diagnoses, setDiagnoses] = useState<HubDiagnosis[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const lastDeleted = useRef<DeletedItem | null>(null);

    // ── Fetch ──

    const fetchHub = useCallback(async () => {
        if (!visitId) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const data = await fetchDiagnosisHub(visitId);
            setDiagnoses(
                data.diagnoses.map((d) => ({
                    icdCode: d.icdCode,
                    description: d.description,
                    classification: d.classification as DiagnosisClassification,
                    sources: d.sources.map((src) => ({
                        type: src.type,
                        label: src.label,
                        sourceId: src.sourceId
                    }))
                }))
            );
        } catch (e) {
            setError(e instanceof Error ? e.message : "Failed to load diagnoses");
        } finally {
            setLoading(false);
        }
    }, [visitId]);

    // ── Delete (optimistic) ──

    const removeDiagnosis = useCallback(
        (icdCode: string, source: DiagnosisSource) => {
            setDiagnoses((prev) => {
                const idx = prev.findIndex((d) => d.icdCode === icdCode);
                if (idx === -1) {
                    return prev;
                }

                const dx = prev[idx];
                lastDeleted.current = { diagnosis: { ...dx }, source, index: idx };

                // If the diagnosis has multiple sources, just remove this source
                if (dx.sources.length > 1) {
                    const updated = [...prev];
                    updated[idx] = {
                        ...dx,
                        sources: dx.sources.filter((s) => s.sourceId !== source.sourceId)
                    };
                    return updated;
                }

                // Single source — remove the entire diagnosis
                return prev.filter((_, i) => i !== idx);
            });

            // Fire API call in background
            if (source.type === "section") {
                deleteVisitDiagnosisById(visitId, source.sourceId).catch(() => {
                    // On failure, undo will still work from lastDeleted ref
                    setError("Failed to delete diagnosis. Tap UNDO to restore.");
                });
            }
            // Agency soft-delete would need a separate endpoint — skipped for V1
        },
        [visitId]
    );

    // ── Undo ──

    const undoDelete = useCallback(() => {
        const item = lastDeleted.current;
        if (!item) {
            return;
        }

        setDiagnoses((prev) => {
            // Check if the ICD code still exists (multi-source case)
            const existingIdx = prev.findIndex((d) => d.icdCode === item.diagnosis.icdCode);
            if (existingIdx !== -1) {
                // Re-add the source back
                const updated = [...prev];
                updated[existingIdx] = {
                    ...updated[existingIdx],
                    sources: [...updated[existingIdx].sources, item.source]
                };
                return updated;
            }

            // Re-insert the full diagnosis at original position
            const next = [...prev];
            next.splice(Math.min(item.index, next.length), 0, item.diagnosis);
            return next;
        });

        lastDeleted.current = null;

        // Re-create on server: refetch is simplest (the saveDiagnoses endpoint
        // handles dedup, so re-fetching gets the correct state)
        fetchHub().catch(() => {});
    }, [fetchHub]);

    // ── Change Classification (optimistic) ──

    const changeClassification = useCallback(
        (icdCode: string, newClassification: DiagnosisClassification) => {
            setDiagnoses((prev) =>
                prev.map((d) => {
                    if (d.icdCode === icdCode) {
                        return { ...d, classification: newClassification };
                    }
                    return d;
                })
            );

            // Find the diagnosis and update each source via API
            const dx = diagnoses.find((d) => d.icdCode === icdCode);
            if (dx) {
                for (const source of dx.sources) {
                    updateDiagnosisClassification(
                        visitId,
                        source.type,
                        source.sourceId,
                        newClassification
                    ).catch(() => {
                        setError("Failed to update classification");
                    });
                }
            }
        },
        [visitId, diagnoses]
    );

    return {
        diagnoses,
        count: diagnoses.length,
        loading,
        error,
        clearError: () => setError(null),
        fetchHub,
        removeDiagnosis,
        undoDelete,
        changeClassification
    };
}
